import {
  EXPLORATION_BIOME_IDS,
  type ExplorationAreaState,
  type ExplorationBiomeId,
  type ExplorationState,
} from "./explorationTypes";
import { EXPLORATION_MP_SLOT_SEQUENCE } from "./explorationDefinitions";

export function createDefaultExplorationState(): ExplorationState {
  return {
    biomeLevel: 1,
    purchasedMpSlotCount: 0,
    cumulativeTimAddedMs: 0,
    timAccelerationEnabled: false,
    areas: createDefaultExplorationAreas(),
  };
}

export function cloneExplorationState(
  state: ExplorationState,
): ExplorationState {
  return normalizeExplorationState(state);
}

export function normalizeExplorationState(value: unknown): ExplorationState {
  const source = isRecord(value) ? value : {};
  const sourceAreas = isRecord(source.areas) ? source.areas : {};
  return {
    biomeLevel: normalizePositiveSafeInteger(source.biomeLevel, 1),
    purchasedMpSlotCount: Math.min(
      normalizeNonNegativeSafeInteger(source.purchasedMpSlotCount, 0),
      EXPLORATION_MP_SLOT_SEQUENCE.length,
    ),
    cumulativeTimAddedMs: normalizeFiniteNonNegative(
      source.cumulativeTimAddedMs,
      0,
    ),
    timAccelerationEnabled: source.timAccelerationEnabled === true,
    areas: Object.fromEntries(
      EXPLORATION_BIOME_IDS.map((biomeId) => [
        biomeId,
        normalizeExplorationAreaState(sourceAreas[biomeId]),
      ]),
    ) as Record<ExplorationBiomeId, ExplorationAreaState>,
  };
}

function createDefaultExplorationAreas(): Record<
  ExplorationBiomeId,
  ExplorationAreaState
> {
  return Object.fromEntries(
    EXPLORATION_BIOME_IDS.map((biomeId) => [biomeId, { progressMs: 0 }]),
  ) as Record<ExplorationBiomeId, ExplorationAreaState>;
}

function normalizeExplorationAreaState(value: unknown): ExplorationAreaState {
  const source = isRecord(value) ? value : {};
  return {
    progressMs: normalizeFiniteNonNegative(source.progressMs, 0),
  };
}

function normalizePositiveSafeInteger(
  value: unknown,
  fallback: number,
): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0
    ? value
    : fallback;
}

function normalizeNonNegativeSafeInteger(
  value: unknown,
  fallback: number,
): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : fallback;
}

function normalizeFiniteNonNegative(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : fallback;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
